import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { CetVendorDetailsComponent } from '../cet-vendor-details/cet-vendor-details.component';
import { BehaviorSubject, debounceTime, Subject } from 'rxjs';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { EventViewModel } from '../../../models-views/event.view.model';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { AddAdvancePaymentComponent } from './add-advance-payment/add-advance-payment.component';
import { AdvancePaymentResponseModel, PaymentResponseModel } from '../../../models/event.model';
import { VendorFinancialStatsResponseModel } from '../../../models/vendor.model';
import { AdvancedPaymentDetailsComponent } from '../../advanced-payment-details/advanced-payment-details.component';
import {
  ApexChart,
  ApexLegend,
  ApexNonAxisChartSeries,
  ApexResponsive,
  ApexTooltip,
  ApexStroke
} from 'ng-apexcharts';

@Component({
  selector: 'app-advanced-payments',
  standalone: false,
  templateUrl: './advanced-payments.component.html',
  styleUrl: './advanced-payments.component.scss'
})
export class AdvancedPaymentsComponent implements OnChanges, OnInit {

  @Input() eventId: null | string = null;
  loading = false
  paymentList = [];
  advancePaymentResponseModel = new BehaviorSubject<AdvancePaymentResponseModel>(new AdvancePaymentResponseModel())
  vendorFinancialStatsResponseModel = new BehaviorSubject<VendorFinancialStatsResponseModel>(new VendorFinancialStatsResponseModel())
  cetPaymentModalRef:any
  rfqRef?: NzModalRef;
  searchChange$ = new Subject();
  searchText: string ;
  private lastLoadedEventId: string | null = null;
  private lastSearchText: string = '';
  private initialLoadTimer: ReturnType<typeof setTimeout> | null = null;
  // Summary/chart state (demo data for now)
  vendorSummary: Array<{ id: number | string; vendor: string; quotation_amount: number; total_bill: number; total_paid: number; total_due: number; avatarColor?: string }>=[];
  summaryLoading = false;
  vendorId :any="";
  eventBudget: number | null = null;
  // Keep raw stats to support per-vendor, per-payment pie breakdowns
  private vendorStatsRaw: any[] = [];
  // derived chart data presence flag
  chartHasData = false;

  // ApexCharts pie config
  pieSeries: ApexNonAxisChartSeries = [];
  pieChart: ApexChart = { type: 'pie', width: '100%', height: 200 } as ApexChart;
  pieLabels: string[] = [];
  pieLegend: ApexLegend = { position: 'bottom', show: false };
  // add stroke to improve visibility of very small slices
  pieStroke: ApexStroke = { width: 1, colors: ['#ffffff'] } as ApexStroke;
  pieResponsive: ApexResponsive[] = [
    {
      breakpoint: 768,
      options: {
        chart: { width: '100%', height: 200 },
        legend: { position: 'bottom', show: false }
      }
    }
  ];
  // Enable built-in tooltip and format value as USD amount paid
  pieTooltip: ApexTooltip = {
    enabled: true,
    y: {
      formatter: (val: number) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(val) || 0)
    }
  };
  // simple color palette for chart slices and tooltip bullets
  pieColors: string[] = ['#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0', '#546E7A'];
  // computed vertical tooltip/legend items
  pieLegendItems: Array<{ label: string; percentage: number; color: string }> = [];
  // keep track of tiny slices (< 1%) to inform the user
  tinySlices: string[] = [];
  // total paid (USD) for chart summary label
  totalPaidUSD: number = 0;
  // Manual pagination for vendor summary table
  vendorPage: number = 1;
  vendorPageSize: number = 5;

  // Avatar color palette to randomize vendor badges (avoid consecutive duplicates)
  private avatarColors: string[] = [
    '#f56a00', '#7265e6', '#ffbf00', '#00a2ae',
    '#13c2c2', '#eb2f96', '#52c41a', '#fa541c'
  ];

  get vendorTotalPages(): number {
    return Math.max(1, Math.ceil((this.vendorSummary?.length || 0) / this.vendorPageSize));
  }

  get vendorPageWindowStart(): number {
    return Math.floor((this.vendorPage - 1) / 4) * 4 + 1;
  }

  get vendorPageWindow(): number[] {
    const start = this.vendorPageWindowStart;
    const end = Math.min(start + 3, this.vendorTotalPages);
    const arr: number[] = [];
    for (let i = start; i <= end; i++) arr.push(i);
    return arr;
  }

  get pagedVendorSummary(): Array<{ id: number | string; vendor: string; quotation_amount: number; total_bill: number; total_paid: number; total_due: number; avatarColor?: string }>{
    const start = (this.vendorPage - 1) * this.vendorPageSize;
    return this.vendorSummary.slice(start, start + this.vendorPageSize);
  }

  goToVendorPage(n: number): void {
    if (n >= 1 && n <= this.vendorTotalPages) {
      this.vendorPage = n;
    }
  }

  prevVendorPage(): void {
    if (this.vendorPage > 1) {
      this.vendorPage -= 1;
    }
  }

  nextVendorPage(): void {
    if (this.vendorPage < this.vendorTotalPages) {
      this.vendorPage += 1;
    }
  }

  // Deterministic avatar color based on name to keep consistent coloring across tables
  getAvatarColor(name?: string): string {
    const fallback = '#1890ff';
    if (!name || !this.avatarColors?.length) return fallback;
    // simple hash of the string to index into palette
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = (hash << 5) - hash + name.charCodeAt(i);
      hash |= 0; // Convert to 32bit integer
    }
    const idx = Math.abs(hash) % this.avatarColors.length;
    return this.avatarColors[idx];
  }

  nextVendorWindow(): void {
    const nextStart = this.vendorPageWindowStart + 4;
    if (nextStart <= this.vendorTotalPages) {
      this.vendorPage = nextStart;
    }
  }
  constructor( private modalService: NzModalService, private vm: EventViewModel,  private route: ActivatedRoute
  ) {
    this.vm.advancedPaymentsComponent = this
      this.searchChange$.pipe(debounceTime(500)).subscribe(name => {
        this.getData()
      });
  }

  ngOnInit(): void {
    this.vm.subscribeToAdvancePayment(this.advancePaymentResponseModel)
    this.vm.subscribeToVendorFinancialStats(this.vendorFinancialStatsResponseModel)
    this.scheduleInitialLoad();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['eventId'] && changes['eventId'].currentValue) {
      this.scheduleInitialLoad();
    }
  }

  private scheduleInitialLoad(): void {
    if (!this.eventId) {
      return;
    }

    if (this.initialLoadTimer) {
      clearTimeout(this.initialLoadTimer);
    }

    // Coalesce rapid input/lifecycle changes into a single initial API fetch.
    this.initialLoadTimer = setTimeout(() => {
      this.initialLoadTimer = null;
      if (!this.eventId) {
        return;
      }
      const currentEventId = String(this.eventId);
      if (this.lastLoadedEventId === currentEventId) {
        return;
      }
      this.lastLoadedEventId = currentEventId;
      this.getData();
      this.loadVendorSummary();
    }, 250);
  }

  getData(){
    if (!this.eventId) {
      this.loading = false;
      return;
    }
    this.loading = true
    this.vm.getadvancePayments(1, 100, this.eventId, this.searchText,this.vendorId)
  }
  onSearch() {
    const currentSearch = (this.searchText ?? '').trim();
    // Ignore the initial empty emission from ngModelChange to avoid an extra API call.
    if (currentSearch === this.lastSearchText) {
      return;
    }
    this.lastSearchText = currentSearch;
    this.searchChange$.next(currentSearch);
  }

  paymentRecieved(){
    this.paymentList = this.advancePaymentResponseModel.value.results;
    this.loading = false
  }

  onAdd(){
     this.cetPaymentModalRef =  this.modalService.create({
        nzTitle: 'Add Payment',
        nzContent: AddAdvancePaymentComponent,
        nzFooter:null,
        nzWidth: '800px',
        nzData: {
          id: this.eventId   // 👈 This is how you pass data
         },
        nzClosable: true,
        // nzOnOk: () => new Promise(resolve => setTimeout(resolve, 1000))
      });

      this.cetPaymentModalRef.afterClose.subscribe(result => {
        if (result === true) {
          this.getData();
        }
        });
    }

 getFileName(file_url:string): string {
    return file_url.split('/').pop()?.split('?')[0] || '';
  }

  getFileDownloadLink(file_url:string): string {
      return `${environment.api_url}${file_url}`
  }

  hasTransactionId(transactionId: string | null | undefined): boolean {
    return !!transactionId?.trim();
  }

  openPaymentDetails(payment: { id?: number | null; transaction_id?: string | null }): void {
    if (!payment?.id || !this.hasTransactionId(payment.transaction_id)) {
      return;
    }

    this.onDetailsOpen(payment.id);
  }

  onDetailsOpen(id:number){
       this.cetPaymentModalRef = this.modalService.create({
          nzTitle: 'Payment Details',
          nzContent: AdvancedPaymentDetailsComponent,
          nzFooter:null,
          nzData: {
          id: id  , name:'' // 👈 This is how you pass data
         },
          nzWidth: '800px',
          nzStyle:{top:'50px'},
          nzClosable: true,
          // nzOnOk: () => new Promise(resolve => setTimeout(resolve, 1000))
        });

        this.cetPaymentModalRef.afterClose.subscribe(result => {
          if (result === true) {
            this.getData();
          }
    });
}

  deletePayment(id:number){
    this.loading = true
    this.vm.deleteAdvancePaymentById(id)
  }

  paymentDeleted(){
    this.loading = false
    this.getData()
  }

  // --- Vendor filter interactions ---
  selectVendor(v: { id: number | string }): void {
    if (!v || v.id == null) { return; }
    this.vendorId = v.id;
    // Update the pie chart immediately for the selected vendor and refresh payment list
    this.applyVendorFilterToPie();
    this.getData();
  }

  clearVendorFilter(event?: MouseEvent): void {
    if (event) { event.stopPropagation(); }
    this.vendorId = '';
    // Reset pie chart to full summary and refresh payment list
    this.applyVendorFilterToPie();
    this.getData();
  }

  // Load vendor summary via API
  private loadVendorSummary() {
    if (!this.eventId) {
      this.summaryLoading = false;
      this.chartHasData = false;
      return;
    }
    this.summaryLoading = true;
    this.chartHasData = false;
    this.vm.getVendorFinancialStats(this.eventId);
  }

  // Callback triggered by VM on data arrival
  vendorStatsReceived() {
    const res: any = this.vendorFinancialStatsResponseModel.value;

    const stats: any[] = Array.isArray(res?.vendors) ? res.vendors : [];
    this.eventBudget = res?.event_budget != null ? Number(res.event_budget) || 0 : null;
    // store raw for later per-vendor breakdown
    this.vendorStatsRaw = stats;
    this.vendorSummary = stats.map((d: any) => {
      const vendor_id = d.id;
      const vendor_name = d.name;
      const quotation_amount = Number(d.quotation_amount_usd) || 0;
      const total_bill = Number(d.invoice_amount_usd) || 0;
      const total_paid = Number(d.payment_amount_usd) || 0;
      const total_due = total_bill - total_paid;

      let data = {
        id: vendor_id,
        vendor: vendor_name,
        quotation_amount,
        total_bill,
        total_paid,
        total_due
      }

      return data;

    });

    // Assign random avatar background colors ensuring no two consecutive are the same
    let lastIdx = -1;
    this.vendorSummary.forEach(v => {
      if (!this.avatarColors.length) { v.avatarColor = '#1890ff'; return; }
      let idx = Math.floor(Math.random() * this.avatarColors.length);
      if (idx === lastIdx) {
        idx = (idx + 1) % this.avatarColors.length;
      }
      v.avatarColor = this.avatarColors[idx];
      lastIdx = idx;
    });

    // Build pie chart from total_paid by vendor with max 10 slices.
    // If more than 10 vendors, take top 9 individually and group the rest as 'Others'.
    const sortedByPaid = [...this.vendorSummary].sort((a, b) => b.total_paid - a.total_paid);
    let labels: string[] = [];
    let series: number[] = [];
    if (sortedByPaid.length <= 10) {
      labels = sortedByPaid.map(v => v.vendor);
      series = sortedByPaid.map(v => v.total_paid);
    } else {
      const top9 = sortedByPaid.slice(0, 9);
      const others = sortedByPaid.slice(9);
      const othersSum = others.reduce((acc, v) => acc + (Number(v.total_paid) || 0), 0);
      labels = [...top9.map(v => v.vendor), 'Others'];
      series = [...top9.map(v => v.total_paid), othersSum];
    }

    this.pieLabels = labels;
    this.pieSeries = series;

    // Apply current vendor filter (if any) to pie; otherwise compute default legend/summary
    this.applyVendorFilterToPie();

    this.summaryLoading = false;
    // reset vendor table pagination on new data
    this.vendorPage = 1;
  }

  // Recompute pie + legend based on current vendorId selection
  private applyVendorFilterToPie(): void {
    if (!Array.isArray(this.vendorSummary)) { this.vendorSummary = []; }

    if (this.vendorId) {
      const selectedFromSummary = this.vendorSummary.find(v => (v.id as any) == (this.vendorId as any));
      if (selectedFromSummary) {
        this.pieLabels = [selectedFromSummary.vendor];
        this.pieSeries = [Number(selectedFromSummary.total_paid) || 0];
      } else {
        this.rebuildPieFromSummary();
      }
    } else {
      this.rebuildPieFromSummary();
    }

    // Recompute legend items and totals from current pie data
    const sumPaid = this.pieSeries.reduce((a: number, b: number) => a + (Number(b) || 0), 0);
    this.totalPaidUSD = sumPaid;
    this.chartHasData = sumPaid > 0;
    const denom = sumPaid > 0 ? sumPaid : 1;
    this.pieLegendItems = this.pieLabels.map((label, idx) => ({
      label,
      percentage: Math.round((((Number(this.pieSeries[idx]) || 0) / denom) * 1000)) / 10,
      color: this.pieColors[idx % this.pieColors.length]
    }));
    // compute tiny slices list (< 1%) to display a helpful note
    this.tinySlices = this.pieLegendItems
      .filter(it => it.percentage > 0 && it.percentage < 1)
      .map(it => `${it.label} (${it.percentage}%)`);
  }

  // Build the default (unfiltered) pie from the full vendorSummary with top 9 + Others grouping
  private rebuildPieFromSummary(): void {
    const sortedByPaid = [...this.vendorSummary].sort((a, b) => b.total_paid - a.total_paid);
    if (sortedByPaid.length <= 10) {
      this.pieLabels = sortedByPaid.map(v => v.vendor);
      this.pieSeries = sortedByPaid.map(v => v.total_paid);
    } else {
      const top9 = sortedByPaid.slice(0, 9);
      const others = sortedByPaid.slice(9);
      const othersSum = others.reduce((acc, v) => acc + (Number(v.total_paid) || 0), 0);
      this.pieLabels = [...top9.map(v => v.vendor), 'Others'];
      this.pieSeries = [...top9.map(v => v.total_paid), othersSum];
    }
  }

  onVendorDetails(id:number) {
    this.rfqRef = this.modalService.create({
          nzTitle: 'Vendor Details',
          nzContent: CetVendorDetailsComponent,
          nzFooter: null,
          nzWidth:'90vw',
          nzStyle: {
              top: '40px',
          },
          nzData: {
            id: id
          }
        });
  }

  editPayment(data:any){
    this.cetPaymentModalRef =  this.modalService.create({
        nzTitle: 'Edit Payment',
        nzContent: AddAdvancePaymentComponent,
        nzFooter:null,
        nzWidth: '800px',
        nzStyle:{top:'50px'},
        nzData:{data:data},
        nzClosable: true,
        // nzOnOk: () => new Promise(resolve => setTimeout(resolve, 1000))
      });

      this.cetPaymentModalRef.afterClose.subscribe(result => {
        if (result === true) {
          this.getData();
        }
        });
  }
}
